import { useEffect, useRef, useState } from 'react'
import { useStore } from '@tanstack/react-form'
import { toast } from 'sonner'
import { FileCheck2, Loader2, Plus, Search, Trash2 } from 'lucide-react'
import { Dialog, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormDialogContent } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { getFieldError } from '@/components/clientes/wizard/useClienteForm'
import { useConsultarCuitAFIP } from '@/hooks/useConsultarCuitAFIP'
import { formatCurrency } from '@/lib/utils'
import { etiquetaComprobante, type FacturaResumen } from '@/services/facturasService'
import {
  CONDICIONES_IVA_RECEPTOR,
  domicilioDelPadron,
  LINEA_VACIA,
  nombreDelPadron,
  parsearImporte,
  useFacturaManualForm,
  type CondicionIvaReceptor,
  type FacturaManualFormValues,
} from './forms'

interface NuevaFacturaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** CUIT del emisor del estudio: el form no deja facturarle a ese mismo CUIT. */
  cuitEmisor?: string | null
  /** `solicitudId` es el id de la factura que crea el servidor. Se repite hasta que una emisión responde bien. */
  onEmitir: (values: FacturaManualFormValues, solicitudId: string) => Promise<void>
  /** Busca la factura de una solicitud pendiente. Sin esta función, la solicitud pendiente se reusa siempre. */
  buscarFactura?: (solicitudId: string) => Promise<Pick<FacturaResumen, 'clase' | 'cbte_tipo' | 'pto_vta' | 'numero'> | null>
}

const CLAVE_SOLICITUD = 'facturas:solicitud-pendiente'

/** La solicitud enviada que todavía no tuvo respuesta, en sessionStorage. Sin storage, la guarda solo el componente. */
const solicitudGuardada = {
  leer: (): string | null => {
    try { return sessionStorage.getItem(CLAVE_SOLICITUD) } catch { return null }
  },
  guardar: (id: string | null) => {
    try { if (id) sessionStorage.setItem(CLAVE_SOLICITUD, id); else sessionStorage.removeItem(CLAVE_SOLICITUD) } catch { /* sin storage */ }
  },
}

const ErrorDeCampo = ({ errors }: { errors: unknown[] }) =>
  errors.length > 0 ? <p className="text-sm text-destructive">{getFieldError(errors)}</p> : null

/** Factura a alguien que no es cliente del estudio, o por algo que no es un cobro (ORR-255 fase 3). */
export function NuevaFacturaDialog({ open, onOpenChange, cuitEmisor, onEmitir, buscarFactura }: NuevaFacturaDialogProps) {
  const [aConfirmar, setAConfirmar] = useState<FacturaManualFormValues | null>(null)
  const { form } = useFacturaManualForm({ onSubmit: (v) => setAConfirmar(v), cuitEmisor })
  const isDirty = useStore(form.store, (s) => s.isDirty)
  const lineas = useStore(form.store, (s) => s.values.lineas)
  const total = lineas.reduce((s, l) => s + (parsearImporte(l.importe) || 0), 0)
  const padron = useConsultarCuitAFIP({
    onSuccess: (data) => {
      if (!data.encontrado || !data.persona) return
      const p = data.persona
      const nombre = nombreDelPadron(p)
      if (nombre) form.setFieldValue('nombre', nombre)
      form.setFieldValue('condicionIva', p.condicionIVA as CondicionIvaReceptor)
      const domicilio = domicilioDelPadron(p)
      if (domicilio) form.setFieldValue('domicilio', domicilio)
    },
  })
  const [emitiendo, setEmitiendo] = useState(false)
  const pendiente = useRef<string | null>(null)

  const olvidarPendiente = () => {
    pendiente.current = null
    solicitudGuardada.guardar(null)
  }

  // Una solicitud sin respuesta se reusa. Si su factura ya existe, se avisa y la próxima solicitud es nueva.
  useEffect(() => {
    if (!open) return
    form.reset()
    setAConfirmar(null)
    const id = pendiente.current ?? solicitudGuardada.leer()
    pendiente.current = id
    if (!id || !buscarFactura) return
    buscarFactura(id)
      .then((f) => {
        if (!f || pendiente.current !== id) return
        olvidarPendiente()
        toast.info(`La factura que pediste antes ya está registrada${etiquetaComprobante(f) ? `: ${etiquetaComprobante(f)}` : ''}. La ves en el listado.`)
      })
      .catch(() => {})
  }, [open, form, buscarFactura])

  const confirmar = async () => {
    if (!aConfirmar) return
    setEmitiendo(true)
    const solicitudId = pendiente.current ?? crypto.randomUUID()
    pendiente.current = solicitudId
    solicitudGuardada.guardar(solicitudId)
    try {
      await onEmitir(aConfirmar, solicitudId)
      olvidarPendiente()
      setAConfirmar(null)
      onOpenChange(false)
    } catch {
      // El error ya lo muestra la mutación. El formulario queda abierto, con la misma solicitud, para reintentar.
    } finally {
      setEmitiendo(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => onOpenChange(false)}
        className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck2 className="h-5 w-5" />
            Nueva factura
          </DialogTitle>
          <DialogDescription>
            Para facturar a alguien que no es cliente del estudio. La factura de un cobro se emite desde Movimientos.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-5"
        >
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium mb-1">Receptor</legend>
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
              <form.Field name="tipoDoc">
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="factura-tipo-doc">Documento</Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(v) => {
                        field.handleChange(v as FacturaManualFormValues['tipoDoc'])
                        if (v === 'ninguno') {
                          form.setFieldValue('docNro', '')
                          form.setFieldValue('condicionIva', 'consumidor_final')
                          if (!form.getFieldValue('nombre')) form.setFieldValue('nombre', 'Consumidor final')
                        }
                      }}
                    >
                      <SelectTrigger id="factura-tipo-doc">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cuit">CUIT</SelectItem>
                        <SelectItem value="dni">DNI</SelectItem>
                        <SelectItem value="ninguno">Sin identificar</SelectItem>
                      </SelectContent>
                    </Select>
                    <ErrorDeCampo errors={field.state.meta.errors} />
                  </div>
                )}
              </form.Field>
              <form.Subscribe selector={(s) => s.values.tipoDoc}>
                {(tipoDoc) =>
                  tipoDoc === 'ninguno' ? (
                    <p className="text-sm text-muted-foreground self-end pb-2">
                      Consumidor final sin identificar, hasta {formatCurrency(10_000_000)}.
                    </p>
                  ) : (
                    <form.Field name="docNro">
                      {(field) => (
                        <div className="space-y-1.5">
                          <Label htmlFor="factura-doc-nro">{tipoDoc === 'cuit' ? 'CUIT' : 'DNI'}</Label>
                          <div className="flex gap-2">
                            <Input
                              id="factura-doc-nro"
                              inputMode="numeric"
                              value={field.state.value}
                              onChange={(e) => field.handleChange(e.target.value)}
                              onBlur={field.handleBlur}
                            />
                            {tipoDoc === 'cuit' && (
                              <Button
                                type="button"
                                variant="outline"
                                disabled={padron.isLoading || field.state.value.replace(/\D/g, '').length !== 11}
                                onClick={() => padron.consultarCuit(field.state.value.replace(/\D/g, ''))}
                              >
                                {padron.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                <span className="ml-2">Buscar en ARCA</span>
                              </Button>
                            )}
                          </div>
                          <ErrorDeCampo errors={field.state.meta.errors} />
                        </div>
                      )}
                    </form.Field>
                  )
                }
              </form.Subscribe>
            </div>

            <form.Field name="nombre">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="factura-nombre">Nombre o razón social</Label>
                  <Input
                    id="factura-nombre"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  <ErrorDeCampo errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <form.Field name="condicionIva">
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="factura-cond-iva">Condición frente al IVA</Label>
                    <Select value={field.state.value} onValueChange={(v) => field.handleChange(v as CondicionIvaReceptor)}>
                      <SelectTrigger id="factura-cond-iva">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDICIONES_IVA_RECEPTOR.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <ErrorDeCampo errors={field.state.meta.errors} />
                  </div>
                )}
              </form.Field>
              <form.Field name="domicilio">
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="factura-domicilio">Domicilio</Label>
                    <Input
                      id="factura-domicilio"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                    <ErrorDeCampo errors={field.state.meta.errors} />
                  </div>
                )}
              </form.Field>
            </div>
          </fieldset>

          <form.Field name="lineas" mode="array">
            {(lineasField) => (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium mb-1">Detalle</legend>
                {lineasField.state.value.map((_, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_110px_auto] gap-2 items-start">
                    <form.Field name={`lineas[${i}].descripcion`}>
                      {(field) => (
                        <div className="space-y-1">
                          <Input
                            aria-label={`Descripción de la línea ${i + 1}`}
                            placeholder="Honorarios profesionales"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                          <ErrorDeCampo errors={field.state.meta.errors} />
                        </div>
                      )}
                    </form.Field>
                    <form.Field name={`lineas[${i}].importe`}>
                      {(field) => (
                        <div className="space-y-1">
                          <Input
                            aria-label={`Importe de la línea ${i + 1}`}
                            inputMode="decimal"
                            placeholder="Importe"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                          <ErrorDeCampo errors={field.state.meta.errors} />
                        </div>
                      )}
                    </form.Field>
                    <form.Field name={`lineas[${i}].periodo`}>
                      {(field) => (
                        <div className="space-y-1">
                          <Input
                            aria-label={`Período de la línea ${i + 1}`}
                            type="month"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                          <ErrorDeCampo errors={field.state.meta.errors} />
                        </div>
                      )}
                    </form.Field>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Quitar la línea ${i + 1}`}
                      disabled={lineasField.state.value.length === 1}
                      onClick={() => lineasField.removeValue(i)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <ErrorDeCampo errors={lineasField.state.meta.errors} />
                <div className="flex items-center justify-between">
                  <Button type="button" variant="outline" size="sm" onClick={() => lineasField.pushValue({ ...LINEA_VACIA })}>
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar línea
                  </Button>
                  <p className="text-sm">
                    Total <span className="font-semibold">{formatCurrency(total)}</span>
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">Cargá el período solo en los honorarios mensuales. Sin período, la factura va con la fecha del día.</p>
              </fieldset>
            )}
          </form.Field>

          <div className="space-y-2">
            <form.Field name="email">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="factura-email">Email del receptor</Label>
                  <Input
                    id="factura-email"
                    type="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  <ErrorDeCampo errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="enviarEmail">
              {(field) => (
                <div className="flex items-center gap-2">
                  <Checkbox id="factura-manual-enviar" checked={field.state.value} onCheckedChange={(v) => field.handleChange(v === true)} />
                  <label htmlFor="factura-manual-enviar" className="text-sm">
                    Enviarla por mail cuando quede autorizada
                  </label>
                </div>
              )}
            </form.Field>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">Revisar y emitir</Button>
          </DialogFooter>
        </form>

        {/* Adentro del contenido: así Radix la trata como diálogo anidado y no la oculta. */}
        <AlertDialog open={!!aConfirmar} onOpenChange={(abierto) => !abierto && !emitiendo && setAConfirmar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Emitir la factura?</AlertDialogTitle>
              <AlertDialogDescription>
                Se emite ante ARCA una factura a {aConfirmar?.nombre} por {formatCurrency(total)}. Una vez emitida no se
                puede borrar: se anula con una nota de crédito.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={emitiendo}>Volver</AlertDialogCancel>
              <AlertDialogAction
                disabled={emitiendo}
                onClick={(e) => {
                  e.preventDefault()
                  void confirmar()
                }}
              >
                {emitiendo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Emitir factura
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </FormDialogContent>

    </Dialog>
  )
}
